/**
 * Pantalla de acceso.
 *
 * Es lo primero que ve el cajero a las siete de la mañana, y la unica pantalla
 * del sistema que alguien mira sin estar haciendo algo. Ahi es donde vale la
 * pena poner cuidado en la entrada.
 *
 * El movimiento tiene reglas, no es adorno:
 *
 *   - Todo entra SUBIENDO, escalonado de arriba abajo. La vista sigue ese
 *     orden natural y llega al boton al final, que es donde debe terminar.
 *   - Nada dura mas de medio segundo. Un cajero abre esto mil veces al año; una
 *     animacion vistosa la primera vez es un estorbo la vez doscientos.
 *   - Con `prefers-reduced-motion` la pantalla aparece puesta, sin recorrido.
 *
 * El halo del fondo es un degradado radial muy tenue detras de la tarjeta.
 * Da profundidad sin competir con nada, y en las all-in-one baratas —donde los
 * negros se ven planos— es lo que evita que la pantalla parezca apagada.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Play } from 'lucide-react';
import { useSession } from '../../lib/session.js';
import { useBrandingWhenReady } from '../../lib/branding.js';
import { DURATION, EASE, RISE, STAGGER_PARENT } from '../../lib/motion.js';
import { Link, useRouter } from '../../lib/router.js';
import { ROUTES } from '../../routes.js';
import { BrandLogo } from '../../components/BrandLogo.js';
import { SeedRain } from '../../components/SeedRain.js';
import { Alert, Button, Field } from '../../components/ui.js';

/**
 * True en la compilacion de la DEMO PUBLICADA (Netlify), donde no hay servidor.
 *
 * Con la bandera puesta, el acceso trae usuario y contraseña ya escritos y el
 * boton Entrar lleva directo al recorrido de demostracion: quien recibe el
 * enlace entra con un solo toque, sin que nadie le dicte credenciales. Los
 * valores son de utileria y jamas se envian a ningun lado.
 *
 * La bandera se fija en tiempo de COMPILACION (netlify.toml), no en tiempo de
 * ejecucion: la compilacion de la tienda real nunca la lleva, asi que su
 * pantalla de acceso queda exactamente como siempre.
 */
const DEMO_BUILD = import.meta.env['VITE_DEMO'] === '1';

export function LoginScreen(): ReactNode {
  const { login, loginError, isLoggingIn } = useSession();
  const { branding, ready } = useBrandingWhenReady();
  const { navigate } = useRouter();
  const reduced = useReducedMotion();

  // El valor de la demo se deja con forma de correo porque es lo que se ve mejor
  // en el recorrido, no porque haga falta: el campo ya es `type="text"` para que
  // el personal pueda entrar con su nombre de usuario. Antes era `type="email"`
  // y el navegador validaba antes del `onSubmit`, asi que con "dueno" el boton
  // de entrar no hacia absolutamente nada.
  const [username, setUsername] = useState(DEMO_BUILD ? 'duena@tunegocio.mx' : '');
  const [password, setPassword] = useState(DEMO_BUILD ? 'demostracion' : '');

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (DEMO_BUILD) {
      navigate(ROUTES.demo);
      return;
    }
    if (!username.trim() || !password) return;
    /*
     * El rechazo se absorbe A PROPOSITO, y no con `void`.
     *
     * `login` envuelve un `mutateAsync`, que rechaza cuando la contraseña esta
     * mal. Con `void` esa promesa quedaba sin capturar: el navegador reportaba
     * un "unhandled rejection" en cada intento fallido —ruido en la consola de
     * cualquiera que abra las herramientas— y en un entorno que trate esos
     * rechazos como error fatal, tumbaba la pagina en vez de enseñar el aviso.
     *
     * No se pierde nada al descartarlo: el error ya vive en `loginError`, que
     * es lo que la pantalla pinta. Aqui solo hace falta decirle a la promesa
     * que alguien se hizo cargo.
     */
    login(username.trim(), password).catch(() => {});
  };

  // Con movimiento reducido se salta el recorrido, pero la pantalla se ve
  // igual: mismos elementos, mismas posiciones, ya puestas.
  const variants = reduced ? undefined : RISE;
  const transition = { duration: DURATION.normal, ease: EASE.out };

  return (
    /*
     * `py-safe-10 px-safe-5` y no `px-5 py-10 pt-safe pb-safe`.
     *
     * Lo de antes no hacia lo que parecia: las cuatro clases ponen las mismas
     * propiedades y las propias le ganan a las de fabrica, asi que en cualquier
     * aparato sin muesca los 40 px de aire arriba y abajo valian CERO. En una
     * ventana baja —un telefono acostado, media pantalla de portatil— la tarjeta
     * del acceso quedaba pegada a los dos filos, que es la primera cosa que ve
     * quien abre el enlace.
     *
     * Con el piso, es el mayor de los dos: 40 px en escritorio, la muesca real
     * donde la haya. Y los laterales ya no se olvidan al girar el aparato.
     *
     * `h-app` CON DESPLAZAMIENTO ADENTRO, y no `min-h-app`.
     *
     * `min-h-app` es "crece con lo que traiga", y se apoyaba en que la PAGINA se
     * desplazara. En el navegador funcionaba; en la APLICACION INSTALADA no,
     * porque ahi el cuerpo va anclado (`position: fixed; overflow: hidden`) para
     * que iOS no deje jalar todo el documento con el dedo. Medido a 740x380 —un
     * telefono ACOSTADO, que es como se sostiene para teclear con las dos
     * manos—: la pantalla pide 694 px, hay 380, y los 314 que sobran quedaban
     * FUERA y sin forma de alcanzarlos. Entre ellos, la contraseña y el boton de
     * entrar. No se veia como un error: se veia como un acceso donde no se puede
     * entrar.
     *
     * `m-auto` en la tarjeta y NO `items-center` aqui: centrar con alineacion
     * recorta por ARRIBA cuando el contenido no cabe, y lo inalcanzable pasa a
     * ser el logotipo. Los margenes automaticos centran igual mientras sobre
     * espacio y se rinden a cero cuando no.
     *
     * `overflow-x-hidden` porque poner `overflow-y: auto` obliga al eje X a
     * dejar de ser `visible`: sin esto, la lluvia del fondo abriria una barra de
     * desplazamiento horizontal.
     */
    <div className="relative h-app bg-bg flex flex-col px-safe-5 py-safe-10 overflow-y-auto overflow-x-hidden overscroll-contain">
      {/* Halo de ambiente. Decorativo y muy tenue; no lleva significado. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(60rem 32rem at 50% -10%, color-mix(in srgb, var(--color-primary) 9%, transparent), transparent 70%)',
        }}
      />

      {/*
        Lluvia de chiles y semillas. Va sobre el halo y debajo del formulario:
        la tarjeta lleva `relative`, asi que gana sin necesidad de repartir
        `z-index` por toda la pantalla.
      */}
      <SeedRain />

      <motion.div
        className="relative w-full max-w-sm m-auto"
        variants={reduced ? undefined : STAGGER_PARENT}
        initial="hidden"
        animate="visible"
      >
        {/* ----------------------------- Marca ----------------------------- */}
        <motion.div variants={variants} transition={transition}>
          <div className="flex flex-col items-center text-center mb-8">
            {/*
              El radio del contenedor sigue al del sello —15 de 64 unidades del
              lienzo, o sea 23.4 %— para que la sombra caiga sobre la silueta
              real y no sobre un cuadrado invisible con las esquinas de fuera.
            */}
            <span className="inline-flex rounded-[23.4%] shadow-card">
              <BrandLogo className="size-16" animated={!reduced} />
            </span>

            {/*
             * El espacio queda reservado desde el primer cuadro para que la
             * pantalla no de un brinco cuando llegue el nombre. Solo se escribe
             * cuando ya se sabe cual es.
             */}
            <div className="mt-4 min-h-[4.25rem] flex flex-col justify-start">
              {ready ? (
                <motion.div
                  initial={reduced ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: DURATION.quick, ease: EASE.out }}
                >
                  <h1 className="text-2xl font-semibold tracking-tight text-balance">
                    {branding.name}
                  </h1>
                  <p className="text-muted text-sm mt-1 text-pretty">
                    {branding.tagline || 'Punto de venta y control de la tienda'}
                  </p>
                </motion.div>
              ) : null}
            </div>
          </div>
        </motion.div>

        {/* ---------------------------- Formulario ---------------------------- */}
        <form onSubmit={onSubmit} className="space-y-4">
          <motion.div variants={variants} transition={transition}>
            <Field
              /*
               * DICE "USUARIO", NO "CORREO", Y ES `type="text"`.
               *
               * Aqui se entra con el nombre de usuario que le dio la dueña. Que
               * por dentro Supabase Auth autentique por correo es un detalle de
               * la plataforma, no algo que quien atiende el mostrador tenga que
               * saber: a quien no tiene correo se le sintetiza
               * `usuario@<slug>.abarrotes.local` al darlo de alta, y nadie se
               * aprende esa direccion. La traduccion la hace `resolve_login` en
               * la pasarela, y quien si tiene un correo de verdad lo escribe
               * completo y pasa de largo — el campo acepta las dos cosas aunque
               * la etiqueta nombre la que se usa.
               *
               * Con `type="email"` el navegador validaba antes del `onSubmit`,
               * asi que al escribir "rosa" el boton de entrar no hacia
               * absolutamente nada y sin un solo mensaje que lo explicara.
               */
              label="Usuario"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="next"
              required
              error={loginError?.fields?.username}
            />
          </motion.div>

          <motion.div variants={variants} transition={transition}>
            <Field
              label="Contraseña"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              enterKeyHint="go"
              required
              error={loginError?.fields?.password}
            />
          </motion.div>

          {/*
           * El error entra con un desplazamiento corto y hacia abajo, distinto
           * del resto: llama la atencion sin sacudir la pantalla. Sacudir un
           * formulario de acceso se siente a regaño.
           */}
          {loginError && !loginError.fields ? (
            <motion.div
              initial={reduced ? false : { opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: DURATION.quick, ease: EASE.out }}
            >
              <Alert tone={loginError.code === 'ACCOUNT_LOCKED' ? 'warn' : 'danger'}>
                {loginError.message}
              </Alert>
            </motion.div>
          ) : null}

          <motion.div variants={variants} transition={transition}>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              loading={isLoggingIn}
              disabled={!username.trim() || !password}
            >
              Entrar
            </Button>
          </motion.div>
        </form>

        <motion.p
          variants={variants}
          transition={transition}
          className="text-[12px] text-subtle text-center mt-8 text-pretty"
        >
          Si olvidaste tu contraseña, pidele al dueño que te la restablezca desde su cuenta.
        </motion.p>

        {/*
         * La puerta al recorrido de demostracion. Va en oro de la marca y no en
         * jade a proposito: el jade es el color de actuar (cobrar, entrar) y
         * este boton no opera nada — presenta. Es la misma capa de identidad
         * que la lluvia del fondo.
         */}
        <motion.div variants={variants} transition={transition} className="mt-7">
          <div aria-hidden className="h-px bg-line" />
          <Link
            to={ROUTES.demo}
            className="mt-6 flex h-13 items-center justify-center gap-2.5 rounded-full border border-brand-gold/45 px-6 text-[15px] font-semibold text-brand-gold transition-[background-color,border-color] duration-150 hover:bg-brand-gold/10 hover:border-brand-gold/70"
          >
            <Play className="size-4 fill-current" strokeWidth={0} aria-hidden />
            Ver como funciona el sistema
          </Link>
          <p className="mt-3 text-center text-[12px] text-subtle text-pretty">
            Recorrido de demostracion · sin iniciar sesion y con datos de ejemplo
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
